import { useMemo, useState } from "react";
import { useAppDispatch } from "../../../stores/hooks/hooks.ts";
import { useForm, SubmitHandler } from "react-hook-form";
import { setPassword } from "../../../stores/thunks/userAuthThunk.tsx";
import { UserSetPasswordBodyType } from "../../../stores/types/userAuthTypes.tsx";
import {
  RevButton,
  RevInput,
  RevLink,
  RevPasswordStrengthMeter,
  RevSpinner,
} from "@revmatics/catalyst";
import Logo from "/images/logo.svg";
import { passwordRequirements } from "../../../helpers/functions/getPasswordRequirements.ts";
import { useLocation, useNavigate } from "react-router";
import { NotFoundPage } from "../../notFound/NotFoundPage.tsx";

type SetPasswordFormData = UserSetPasswordBodyType;

export const SetPasswordPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<SetPasswordFormData>({
    mode: "onSubmit",
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const urlData = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token");
    const uid = searchParams.get("uid");

    setIsLoading(false);
    if (token && uid) {
      return { token, uid };
    }

    return null;
  }, [location.search]);

  const onFormSubmit: SubmitHandler<SetPasswordFormData> = (data) => {
    setIsSubmitting(true);
    if (urlData) {
      dispatch(
        setPassword({
          body: { ...data, ...urlData },
          callback: () => {
            setIsSubmitting(false);
            setValue("new_password", "");
            setTimeout(() => navigate("/signin"), 3000);
          },
        }),
      );
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }
  if (!isLoading && urlData) {
    return (
      <div className="flex flex-col items-center justify-center w-full">
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          className="w-full space-y-4"
          noValidate
        >
          <div className="mb-4 flex justify-start">
            <img src={Logo} alt="Company Logo" className="h-[35px] w-auto" />
          </div>
          <h3 className="mb-2">Set new password</h3>
          <RevInput
            name="new_password"
            label="New Password"
            type="password"
            placeholder="Set password"
            size="md"
            required
            className="mb-1"
            register={register("new_password", {
              required: "Password is required.",
            })}
            error={errors.new_password}
            enableClearButton={true}
            onClear={() =>
              setValue("new_password", "", { shouldValidate: true })
            }
          />
          <RevPasswordStrengthMeter
            password={watch("new_password", "")}
            requirements={passwordRequirements}
            requirementsInstructions="Your password must be at least 8 characters in length, have at least 1 special character (!@#$%^&*), and must only consist of alphanumeric and special characters"
          />
          <RevButton
            type="submit"
            variant="primary"
            text="Set new password"
            className="w-full"
            isLoading={isSubmitting}
            displayAs="button"
          />
          <div className="flex items-baseline gap-2">
            <p className="typography-body-sm text-monochrome-900">Back to</p>
            <RevLink
              linkFontSize="medium"
              text="Login"
              pathname="/signin"
              underline={false}
            />
          </div>
        </form>
      </div>
    );
  }

  return <NotFoundPage />;
};
